import React, { useState } from 'react'
import { Form, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter, Label, Button, Input, FormFeedback, Alert, Progress } from 'reactstrap'
import { UpdatePromotionTemplateMutation } from './Mutations'
import withApolloClient from '../withApolloClient'
import './AddPromotionModal.css'

const isSunday = (date) => !!date && new Date(date + "T00:00:00").getDay() === 0

const initialInputFor = (promo) => ({
    promoPrice: promo?.promoPrice ?? '',
    minimumPromoPrice: promo?.minimumPromoPrice ?? '',
    maximumPromoPrice: promo?.maximumPromoPrice ?? '',
    cycle: promo?.cycle ?? '',
    minCycle: promo?.minCycle ?? '',
    maxCycle: promo?.maxCycle ?? '',
    carYearMin: promo?.carYearMin ?? '',
    carYearMax: promo?.carYearMax ?? '',
    startDate: promo?.startDate ?? '',
    expirationDate: promo?.expirationDate ?? '',
    isActive: !!promo?.isActive,
    visibleToCustomers: !!promo?.visibleToCustomers,
})

const EditPromotionModal = (props) => {
    const promo = props.promo || {}
    const [input, setInput] = useState(() => initialInputFor(props.promo))
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const handleChange = (e) => {
        const { name, type, checked, value } = e.target
        setInput((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
    }

    const validationError = () => {
        if (input.minimumPromoPrice !== '' && parseFloat(input.minimumPromoPrice) > parseFloat(input.promoPrice)) {
            return 'Minimum Value must be less than the default Value'
        }
        if (input.maximumPromoPrice !== '' && parseFloat(input.maximumPromoPrice) < parseFloat(input.promoPrice)) {
            return 'Maximum Value must be higher than the default Value'
        }
        if (input.minCycle !== '' && parseFloat(input.minCycle) > parseFloat(input.cycle)) {
            return 'Minimum Cycle must be less than the default Cycle'
        }
        if (input.maxCycle !== '' && parseFloat(input.maxCycle) < parseFloat(input.cycle)) {
            return 'Maximum Cycle must be higher than the default Cycle'
        }
        if (input.carYearMin !== '' && input.carYearMax !== '' && parseInt(input.carYearMin) > parseInt(input.carYearMax)) {
            return 'Minimum Car Year must be less than or equal to the Maximum Car Year'
        }
        if (input.startDate && input.expirationDate && input.startDate > input.expirationDate) {
            return 'Expiration Date must be on or after the Start Date'
        }
        if (input.expirationDate && !isSunday(input.expirationDate)) {
            return 'Expiration Date can only be a Sunday'
        }
        return ''
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        const validation = validationError()
        if (validation) {
            setError(validation)
            return
        }

        const mutationInput = {
            promotionTemplate: promo.id,
            promoPrice: parseFloat(input.promoPrice),
            minimumPromoPrice: parseFloat(input.minimumPromoPrice),
            maximumPromoPrice: parseFloat(input.maximumPromoPrice),
            cycle: parseFloat(input.cycle),
            minCycle: parseFloat(input.minCycle),
            maxCycle: parseFloat(input.maxCycle),
            carYearMin: parseInt(input.carYearMin),
            carYearMax: parseInt(input.carYearMax),
            startDate: input.startDate,
            expirationDate: input.expirationDate,
            isActive: input.isActive,
            visibleToCustomers: input.visibleToCustomers,
        }

        setLoading(true)
        props.apolloClient.mutate({ mutation: UpdatePromotionTemplateMutation, variables: { input: mutationInput } })
            .then((result) => {
                setLoading(false)
                if (result.data && result.data.updatePromotionTemplate && result.data.updatePromotionTemplate.ok) {
                    setError('')
                    props.refetchQuery()
                    props.handleClose()
                } else if (result.data && result.data.updatePromotionTemplate && result.data.updatePromotionTemplate.errors && result.data.updatePromotionTemplate.errors[0]) {
                    setError(result.data.updatePromotionTemplate.errors[0].messages.toString())
                } else {
                    setError('Something went wrong, please contact admin for support!')
                }
            })
            .catch((err) => {
                setLoading(false)
                setError(err.message)
            })
    }

    const isPercentage = !!promo.isPercentage
    const agreementName = promo.agreementType ? promo.agreementType.name : '--'
    const carModelName = promo.carModel ? `${promo.carModel.make} ${promo.carModel.name}` : 'For All Models'
    const tagNames = (promo.tags || []).map((tag) => tag.name)
    const startAfterExpiration = !!(input.startDate && input.expirationDate && input.startDate > input.expirationDate)
    const expirationInvalid = !!input.expirationDate && (startAfterExpiration || !isSunday(input.expirationDate))

    return (
        <Modal isOpen={props.isOpen} className="promotion-modal">
            <ModalHeader
                close={(
                    <button type="button" className="promotion-modal__close" aria-label="Close" onClick={props.handleClose}>
                        ×
                    </button>
                )}
            >
                <span className="promotion-modal__title">Edit Promotion</span>
                <span className="promotion-modal__subtitle">Only the fields you can change today are editable.</span>
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    {(loading || props.loading) && <Progress animated color="info" value="100" />}

                    <div className="promotion-modal__layout">
                        <div className="promotion-modal__form">
                            <section className="promotion-modal__section">
                                <div className="promotion-modal__section-heading">
                                    <span>1</span>
                                    <div>
                                        <h2>Promotion Basics</h2>
                                        <p className="promotion-modal__subtitle">Name and value type are set at creation.</p>
                                    </div>
                                </div>
                                <div className="promotion-modal__grid">
                                    <FormGroup className="promotion-modal__field promotion-modal__span-8">
                                        <Label for="editPromoTitle">Promotion Title</Label>
                                        <Input type="text" id="editPromoTitle" disabled value={promo.title || ''} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                        <Label>Promotion Type</Label>
                                        <div className="promotion-modal__segmented">
                                            <Button type="button" disabled className={!isPercentage ? 'active' : ''}>Flat Price</Button>
                                            <Button type="button" disabled className={isPercentage ? 'active' : ''}>Percentage</Button>
                                        </div>
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-8">
                                        <Label for="editTags">Tags</Label>
                                        <Input type="text" id="editTags" disabled value={tagNames.join(', ')} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                        <Label for="editVisibleToCustomers">Customer Visibility</Label>
                                        <Label className="promotion-modal__visibility" for="editVisibleToCustomers">
                                            <span>Visible during booking</span>
                                            <input className="promotion-modal__toggle" id="editVisibleToCustomers" name="visibleToCustomers" type="checkbox" checked={input.visibleToCustomers} onChange={handleChange} />
                                        </Label>
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                        <Label for="editIsActive">Active</Label>
                                        <Label className="promotion-modal__visibility" for="editIsActive">
                                            <span>Currently active</span>
                                            <input className="promotion-modal__toggle" id="editIsActive" name="isActive" type="checkbox" checked={input.isActive} onChange={handleChange} />
                                        </Label>
                                    </FormGroup>
                                </div>
                            </section>

                            <section className="promotion-modal__section">
                                <div className="promotion-modal__section-heading">
                                    <span>2</span>
                                    <div>
                                        <h2>Eligibility</h2>
                                        <p className="promotion-modal__subtitle">Agreement and car model are fixed; year range can change.</p>
                                    </div>
                                </div>
                                <div className="promotion-modal__grid">
                                    <FormGroup className="promotion-modal__field promotion-modal__span-12">
                                        <Label for="editAgreementType">Agreement Type</Label>
                                        <Input type="text" id="editAgreementType" disabled value={agreementName} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-6">
                                        <Label for="editCarModel">Car Model</Label>
                                        <Input type="text" id="editCarModel" disabled value={carModelName} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-3">
                                        <Label for="editCarYearMin">Minimum Car Year</Label>
                                        <Input type="number" name="carYearMin" id="editCarYearMin" min={2000} max={3000} value={input.carYearMin} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-3">
                                        <Label for="editCarYearMax">Maximum Car Year</Label>
                                        <Input
                                            invalid={!!input.carYearMin && !!input.carYearMax && parseInt(input.carYearMin) > parseInt(input.carYearMax)}
                                            type="number" name="carYearMax" id="editCarYearMax" min={input.carYearMin || 2000} max={3000}
                                            value={input.carYearMax} onChange={handleChange}
                                        />
                                        <FormFeedback>Maximum Car Year must be higher than the Minimum Car Year</FormFeedback>
                                    </FormGroup>
                                </div>
                            </section>

                            <section className="promotion-modal__section">
                                <div className="promotion-modal__section-heading">
                                    <span>3</span>
                                    <div>
                                        <h2>Value &amp; Cadence</h2>
                                        <p className="promotion-modal__subtitle">Interval is fixed; the value and cycle ranges can change.</p>
                                    </div>
                                </div>
                                <div className="promotion-modal__grid">
                                    <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                        <Label for="editPromoPrice">Default Value {isPercentage ? '(%)' : '($)'}</Label>
                                        <Input type="number" step="any" min={0} name="promoPrice" id="editPromoPrice" value={input.promoPrice} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                        <Label for="editMinimumPromoPrice">Minimum Value {isPercentage ? '(%)' : '($)'}</Label>
                                        <Input type="number" step="any" min={0} name="minimumPromoPrice" id="editMinimumPromoPrice" value={input.minimumPromoPrice} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                        <Label for="editMaximumPromoPrice">Maximum Value {isPercentage ? '(%)' : '($)'}</Label>
                                        <Input type="number" step="any" min={0} name="maximumPromoPrice" id="editMaximumPromoPrice" value={input.maximumPromoPrice} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                        <Label for="editCycle">Default Cycle</Label>
                                        <Input type="number" step="any" name="cycle" id="editCycle" value={input.cycle} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                        <Label for="editMinCycle">Minimum Cycle</Label>
                                        <Input type="number" step="any" name="minCycle" id="editMinCycle" value={input.minCycle} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-4">
                                        <Label for="editMaxCycle">Maximum Cycle</Label>
                                        <Input type="number" step="any" name="maxCycle" id="editMaxCycle" value={input.maxCycle} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-6">
                                        <Label for="editIntervalPromo">Interval</Label>
                                        <Input type="text" id="editIntervalPromo" disabled value={promo.interval ?? ''} />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-6">
                                        <Label for="editIntervalUnitPromo">Interval Unit</Label>
                                        <Input type="text" id="editIntervalUnitPromo" disabled value={promo.intervalUnit || ''} />
                                    </FormGroup>
                                </div>
                            </section>

                            <section className="promotion-modal__section">
                                <div className="promotion-modal__section-heading">
                                    <span>4</span>
                                    <div>
                                        <h2>Availability</h2>
                                        <p className="promotion-modal__subtitle">Choose when the promotion can be applied.</p>
                                    </div>
                                </div>
                                <div className="promotion-modal__grid">
                                    <FormGroup className="promotion-modal__field promotion-modal__span-6">
                                        <Label for="editStartDate">Start Date</Label>
                                        <Input
                                            invalid={startAfterExpiration}
                                            type="date" name="startDate" id="editStartDate" value={input.startDate || ''} onChange={handleChange}
                                        />
                                    </FormGroup>
                                    <FormGroup className="promotion-modal__field promotion-modal__span-6">
                                        <Label for="editExpirationDate">Expiration Date</Label>
                                        <Input
                                            invalid={expirationInvalid}
                                            type="date" name="expirationDate" id="editExpirationDate" value={input.expirationDate || ''} onChange={handleChange}
                                        />
                                        {input.expirationDate && !isSunday(input.expirationDate)
                                            ? <FormFeedback>Expiration Date can only be a Sunday</FormFeedback>
                                            : <FormFeedback>Expiration Date must be on or after the Start Date</FormFeedback>}
                                        <p className="promotion-modal__helper promotion-modal__helper--warning">Expiration must fall on a Sunday.</p>
                                    </FormGroup>
                                </div>
                            </section>
                        </div>

                        <aside className="promotion-modal__summary" aria-label="Promotion summary">
                            <h2>Promotion Summary</h2>
                            <p className="promotion-modal__subtitle">Reflects your unsaved changes.</p>
                            <div className="promotion-modal__summary-card">
                                <div className="promotion-modal__summary-value">
                                    <small>{promo.title || 'Default Discount'}</small>
                                    <strong>
                                        {!isPercentage && '$'}{input.promoPrice || '—'}
                                        <span>{isPercentage ? '% percentage' : ' flat price'}</span>
                                    </strong>
                                </div>
                                <dl>
                                    <div>
                                        <dt>Agreement</dt>
                                        <dd>{agreementName}</dd>
                                    </div>
                                    <div>
                                        <dt>Vehicle</dt>
                                        <dd>{carModelName}</dd>
                                    </div>
                                    <div>
                                        <dt>Schedule</dt>
                                        <dd>{input.startDate && input.expirationDate ? `${input.startDate} – ${input.expirationDate}` : 'Not scheduled'}</dd>
                                    </div>
                                    <div>
                                        <dt>Cadence</dt>
                                        <dd>Every {promo.interval} {(promo.intervalUnit || '').toLowerCase()} · cycle {input.cycle || '—'}</dd>
                                    </div>
                                </dl>
                            </div>
                            {tagNames.length > 0 && (
                                <div className="promotion-modal__tags" aria-label="Promotion tags">
                                    {tagNames.map((tag) => <span key={tag}>{tag}</span>)}
                                </div>
                            )}
                        </aside>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="promotion-modal__actions">
                        <Button color="secondary" type="button" onClick={props.handleClose}>Cancel</Button>
                        <Button color="primary" type="submit" disabled={loading}>Save Changes</Button>
                    </div>
                </ModalFooter>
            </Form>
        </Modal>
    )
}

export default withApolloClient(EditPromotionModal)
